import React, { useState, useEffect } from 'react';
import { default as LastSeen } from './LastSeen';
import { default as HonorProgress } from './HonorProgress';
import { default as PlayerListGroup } from './PlayerListGroup';
import { default as TimeChart } from './TimeChart';
import { default as KillsChart } from './KillsChart';
import { default as ScoreChart } from './ScoreChart';
import { default as MapStats } from './MapStats';
import { default as MatchHistory } from '../MatchHistory';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Alert,
    Button, Spinner, UncontrolledTooltip, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem
} from 'reactstrap';
import RestrictedIcon from "../SVG/RestrictedIcon";
import {getTrackerToken} from "../../utils/GetToken";
import {Link} from "react-router-dom";
import ServerBlock from "../Home/ServerBlock";
import Badges from "./Badges";
import ArrowIcon from "../SVG/ArrowIcon";

export default function PlayerInfo({isValid, playerData, mapData, sessionData, loading, nameHistory, goBack}) {

    const [sessionOffset, setSessionOffset] = useState(0);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [canLoadSessions, setCanLoadSessions] = useState(true);
    
    useEffect(() => {
        if (!isValid) return;
        if (!sessionData || sessionData.length < 10) setCanLoadSessions(false);
        
        async function fetchData() {
            const response = await fetch(`api/session/player?name=${encodeURIComponent(playerData.name)}&offset=${sessionOffset}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${getTrackerToken()}`
                }
            }).then(r => r.json());
            
            if (response.length < 10) setCanLoadSessions(false);
            sessionData.push(...response);
        }
        
        if (sessionOffset !== 0) {
            setSessionsLoading(true);
            fetchData().then(() => setSessionsLoading(false));
        }
    }, [sessionOffset]);
    
    if (!isValid) {
        return (
            <Alert color="danger">
                Whoops, there appears to be no results for that player. We may not have seen this player yet.
            </Alert>
        )
    }

    if (playerData.optedOut) {
        return (
            <Alert color="dark" className="text-center">
                <RestrictedIcon />
                <p>This player profile is <span style={{color: 'red'}}>restricted.</span></p>
            </Alert>
        )
    }

    const mapDataCopy = mapData.slice();
    mapData.sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));

    const handleSessionLoadClick = () => {
        setSessionOffset(sessionOffset + 10);
    };

    return (
        <>
        <div className='d-flex w-100 profile-flex'>
            <Card className="d-flex align-items-center justify-content-center col-lg-8 profile-stats-flex">
                <CardBody className="w-100">
                    <div className="d-flex col-lg-8 w-100 pbb">
                        <a className="backbtn mt-1" onClick={goBack}>
                            <ArrowIcon isProfile={true} className="d-flex align-self-start back-profile"
                                       style={{flex: '1', flexBasis: '0'}}/>
                        </a>
                        <div className="d-flex align-items-center justify-content-center"
                             style={{flex: '1', flexBasis: '0'}}>
                            {playerData.onlineServer ?
                                <>
                                    <Link to={`/server/${playerData.onlineServer.address}`}>
                                        <span id="status" className="online-circle"/>
                                    </Link>
                                    <UncontrolledTooltip placement="bottom" target="status" style={{
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none',
                                        maxWidth: '32em',
                                        width: '32em',
                                        textAlign: 'left'
                                    }}>
                                        <ServerBlock key='online' server={playerData.onlineServer} index='online'
                                                     playerName={playerData.name}/>
                                    </UncontrolledTooltip>
                                </>
                                : null}
                            <h1 className="player-name" style={{marginLeft: '5px'}}>
                                {!loading ? playerData.name ?? "Player Not Found" : null}
                            </h1>
                            {nameHistory?.length ?
                                <UncontrolledDropdown direction="down" className="align-self-start">
                                    <DropdownToggle
                                        caret
                                        className="name-history"
                                        tag="a"
                                    >
                                    </DropdownToggle>
                                    <DropdownMenu className="name-history-dropdown dd-name">
                                        <DropdownItem header className="name-history-header">Player Name
                                            History</DropdownItem>
                                        <DropdownItem divider/>
                                        {nameHistory.map((name, index) => (
                                            <DropdownItem text key={index} className="name-history-item">
                                                {name}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                                : null}
                        </div>

                    </div>
                    {<HonorProgress playerData={playerData}/>}
                    {<PlayerListGroup playerData={playerData}/>}
                </CardBody>
                <CardBody className="w-100">
                    <div className="w-100, text-center mb-1">
                        <h2 className="profile-sub-heading yellow gunplay mb-3">MATCH HISTORY</h2>
                    </div>
                    <MatchHistory sessionData={sessionData} isProfile={true} />
                    {sessionData.length >= 10 ?
                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                color="primary"
                                onClick={handleSessionLoadClick}
                                disabled={!canLoadSessions}
                            >
                                {sessionsLoading ?
                                    <Spinner style={{marginRight: '0.5rem'}} className='center' color="information"
                                             size='sm'/> : null
                                }
                                {canLoadSessions ? <>Load More Matches</> : <>All Matches Loaded</>}
                            </Button>
                        </div> : null}
                    <div className="w-100, text-center mt-3">
                        <LastSeen mapData={mapData} sessionData={sessionData}/>
                    </div>
                </CardBody>
            </Card>
            <Card className='column-gap-lg-4 profile-flex-charts justify-content-center ml-1r flex-fill'>
                <div className="d-flex justify-content-center mt-3">
                    <h2 className="profile-sub-heading yellow gunplay">MAP BREAKDOWN</h2>
                </div>
                <CardBody>
                    <div className="charts-container flex-column stat-table-container align-items-center pb-3">
                        {<TimeChart mapData={mapDataCopy} />}
                        {<KillsChart mapData={mapDataCopy}/>}
                        {<ScoreChart mapData={mapDataCopy}/>}
                    </div>
                </CardBody>
            </Card>
        </div>
        <Card
            className='mt-3 w-100'
            style={{
                minHeight: '50vh'
            }}
        >
            <CardBody className="w-100">
                <div className="d-flex justify-content-center">
                    <h2 className="profile-sub-heading yellow gunplay">MAP SPECIFIC</h2>
                </div>
                {<MapStats mapData={mapData}/>}
            </CardBody>
        </Card>
        </>
    )
}